import type { CellKeyDownEvent, SuppressKeyboardEventParams } from "ag-grid-community";
import { z } from "zod";

export const globalKeyboardShortcuts = [
  {
    name: "Shortcut Menu",
    description: "Open the shortcut menu",
    shortcuts: ["shift+?"],
    action: () => {
      alert("Shortcut Menu");
    },
  },
] satisfies TGlobalKeyboardShortcut[];

export const artisTypeSchema = z.enum(["canned", "customer_curve", "eod", "sourced"]);

export type TGridKeyboardShortcut = {
  name: string;
  description: string;
  shortcuts: string[];
  sequenceTimeout?: number;
  action: (params: { params: CellKeyDownEvent; pageId?: string | null }) => void;
};

export type TGlobalKeyboardShortcut = TGridKeyboardShortcut & {
  action: () => void;
};

type TModifierKey = "ctrl" | "alt" | "shift";

export function modifierKeyPressed(event: KeyboardEvent, key: TModifierKey) {
  if (key === "ctrl") return ctrl(event);
  return `${key}Key` in event && event[`${key}Key`];
}

export function keyPressed(event: KeyboardEvent, key: string) {
  return event.key?.toLowerCase() === key?.toLowerCase();
}

export function ctrl(event: KeyboardEvent) {
  return event.ctrlKey || event.metaKey;
}

export function keysMatch(event: KeyboardEvent, shortcutKeys: string) {
  const keys = shortcutKeys.split("+");

  const normalizedKeys = keys.map((k) => (k === "cmd" ? "ctrl" : k));
  const modifiersInShortcut = normalizedKeys.filter((k) => ["ctrl", "alt", "shift"].includes(k));
  const allModifiers = ["ctrl", "alt", "shift"] as const;

  const matches = keys.reduce((keyAcc, key) => {
    const normalizedKey = key === "cmd" ? "ctrl" : key;
    keyAcc.push(
      ["ctrl", "alt", "shift"].includes(normalizedKey)
        ? modifierKeyPressed(event, normalizedKey as TModifierKey)
        : keyPressed(event, key),
    );
    return keyAcc;
  }, [] as boolean[]);

  const noExtraModifiers = allModifiers.every((modifier) => {
    const isInShortcut = modifiersInShortcut.includes(modifier);
    const isPressed = modifierKeyPressed(event, modifier);
    return isInShortcut || !isPressed;
  });

  return matches.every((m) => m) && noExtraModifiers;
}

export function suppressKeyboardEvent(params: SuppressKeyboardEventParams) {
  const event = params.event;

  const modifierPressed = modifierKeyPressed(event, "ctrl") || modifierKeyPressed(event, "alt");
  const arrowPressed = keyPressed(event, "ArrowUp") || keyPressed(event, "ArrowDown");

  // Suppress Ctrl+Arrow keys for increment/decrement
  if (modifierPressed && arrowPressed) {
    return true;
  }

  // Suppress Backspace and Delete on manual entry cells when NOT editing
  // This prevents AG Grid from immediately clearing the cell
  // Instead, we'll handle these keys in onCellKeyDown to enter edit mode
  if (!params.editing && (event.key === "Backspace" || event.key === "Delete")) {
    const status = params.colDef?.headerComponentParams?.status;
    const isManualCell = status === "broadcast" || status === "hybrid_broadcast" || status === "private";

    if (isManualCell) {
      return true;
    }
  }

  return false;
}
